// ChangeDetector.js - Optimized change detection for auto-save
class ChangeDetector {
  constructor() {
    this.lastKnownState = new Map();
    this.changeThreshold = 50; // Minimum characters changed
    this.blockChangeThreshold = 1; // Minimum blocks changed
    this.lastChangeTime = new Map();
    this.minChangeInterval = 1000; // Minimum 1 second between changes
  }

  /**
   * Check if there's a significant change worth saving
   * @param {string} subcategoryId - ID of the subcategory
   * @param {object} newContent - New content to check
   * @param {string} changeType - Type of change (content, privacy, files)
   * @returns {boolean} - Whether the change is significant
   */
  hasSignificantChange(subcategoryId, newContent, changeType = 'content') {
    
    
    const now = Date.now();
    const lastChangeTime = this.lastChangeTime.get(subcategoryId) || 0;
    
    // Rate limiting: don't check changes too frequently
    if (now - lastChangeTime < this.minChangeInterval) {
      return false;
    }

    // Different logic for different change types
    switch (changeType) {
      case 'content':
        return this.hasContentChange(subcategoryId, newContent);
      case 'privacy':
        return this.hasPrivacyChange(subcategoryId, newContent);
      case 'files':
        return this.hasFileChange(subcategoryId, newContent);
      default:
        return this.hasGenericChange(subcategoryId, newContent);
    }
  }

  /**
   * Check for significant content changes
   * @param {string} subcategoryId - ID of the subcategory
   * @param {object} newContent - New content
   * @returns {boolean} - Whether there's a significant content change
   */
  hasContentChange(subcategoryId, newContent) {
    
    const lastState = this.lastKnownState.get(subcategoryId);
    
    if (!lastState) {
      // First observed content for this subcategory. Consider the first
      // user modification significant so auto-save can run immediately.
      this.lastKnownState.set(subcategoryId, this.deepClone(newContent));
      this.lastChangeTime.set(subcategoryId, Date.now());
      return true;
    }

    // If content is plain HTML/string, treat any change as significant
    if (typeof newContent === 'string' || typeof lastState === 'string') {
      const oldString = typeof lastState === 'string' ? lastState : JSON.stringify(lastState);
      const newString = typeof newContent === 'string' ? newContent : JSON.stringify(newContent);

      if (oldString !== newString) {
        this.updateLastState(subcategoryId, newContent);
        return true;
      }
      return false;
    }

    // Quick length check first (fastest)
    const oldLength = this.getContentLength(lastState);
    const newLength = this.getContentLength(newContent);
    const lengthDiff = Math.abs(newLength - oldLength);

    if (lengthDiff < this.changeThreshold) {
      return false; // Change too small
    }

    // Check for structural changes (blocks added/removed/reordered)
    const hasStructuralChange = this.hasStructuralChange(lastState, newContent);
    
    if (hasStructuralChange) {
      this.updateLastState(subcategoryId, newContent);
      return true;
    }

    // Check for content changes within blocks
    const hasContentModification = this.hasContentModification(lastState, newContent);
    
    if (hasContentModification) {
      this.updateLastState(subcategoryId, newContent);
      return true;
    }

    return false;
  }

  /**
   * Check for privacy changes
   * @param {string} subcategoryId - ID of the subcategory
   * @param {string} newPrivacy - New privacy setting
   * @returns {boolean} - Whether privacy changed
   */
  hasPrivacyChange(subcategoryId, newPrivacy) {
    const lastState = this.lastKnownState.get(subcategoryId);
    
    if (!lastState || lastState.privacy !== newPrivacy) {
      this.lastKnownState.set(subcategoryId, { privacy: newPrivacy });
      return true;
    }
    
    return false;
  }

  /**
   * Check for file changes
   * @param {string} subcategoryId - ID of the subcategory
   * @param {Array} newFiles - New files array
   * @returns {boolean} - Whether files changed
   */
  hasFileChange(subcategoryId, newFiles) {
    const lastState = this.lastKnownState.get(subcategoryId);
    
    if (!lastState) {
      this.lastKnownState.set(subcategoryId, { files: this.deepClone(newFiles) });
      return newFiles && newFiles.length > 0;
    }

    const oldFiles = lastState.files || [];
    const newFilesArray = newFiles || [];

    // Quick length check
    if (oldFiles.length !== newFilesArray.length) {
      this.updateLastState(subcategoryId, { files: newFilesArray });
      return true;
    }

    // Check for file changes (by ID or name)
    const oldFileIds = oldFiles.map(f => f.id || f.name).sort();
    const newFileIds = newFilesArray.map(f => f.id || f.name).sort();
    
    if (JSON.stringify(oldFileIds) !== JSON.stringify(newFileIds)) {
      this.updateLastState(subcategoryId, { files: newFilesArray });
      return true;
    }

    return false;
  }

  /**
   * Check for structural changes (blocks added/removed/reordered)
   * @param {object} oldContent - Old content
   * @param {object} newContent - New content
   * @returns {boolean} - Whether there are structural changes
   */
  hasStructuralChange(oldContent, newContent) {
    const oldBlocks = oldContent?.blocks || [];
    const newBlocks = newContent?.blocks || [];
    
    // Check block count
    if (oldBlocks.length !== newBlocks.length) {
      return true;
    }
    
    // Check block IDs and order
    const oldIds = oldBlocks.map(b => b.id).sort();
    const newIds = newBlocks.map(b => b.id).sort();
    
    return JSON.stringify(oldIds) !== JSON.stringify(newIds);
  }

  /**
   * Check for content modifications within blocks
   * @param {object} oldContent - Old content
   * @param {object} newContent - New content
   * @returns {boolean} - Whether there are content modifications
   */
  hasContentModification(oldContent, newContent) {
    const oldBlocks = oldContent?.blocks || [];
    const newBlocks = newContent?.blocks || [];
    
    // Create maps for quick lookup
    const oldBlockMap = new Map(oldBlocks.map(b => [b.id, b]));
    const newBlockMap = new Map(newBlocks.map(b => [b.id, b]));
    
    // Check each block for changes
    for (const [blockId, oldBlock] of oldBlockMap) {
      const newBlock = newBlockMap.get(blockId);
      
      if (!newBlock) continue; // Block was removed (handled by structural change)
      
      // Compare block data
      if (this.hasBlockDataChanged(oldBlock, newBlock)) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Check if block data has changed
   * @param {object} oldBlock - Old block
   * @param {object} newBlock - New block
   * @returns {boolean} - Whether block data changed
   */
  hasBlockDataChanged(oldBlock, newBlock) {
    // Compare type
    if (oldBlock.type !== newBlock.type) {
      return true;
    }
    
    // Compare data (simplified comparison)
    const oldDataStr = JSON.stringify(oldBlock.data || {});
    const newDataStr = JSON.stringify(newBlock.data || {});
    
    return oldDataStr !== newDataStr;
  }

  /**
   * Get content length for quick comparison
   * @param {object} content - Content object
   * @returns {number} - Content length
   */
  getContentLength(content) {
    if (!content) return 0;
    
    if (typeof content === 'string') {
      return content.length;
    }
    
    if (content.blocks) {
      return content.blocks.reduce((total, block) => {
        return total + JSON.stringify(block).length;
      }, 0);
    }
    
    return JSON.stringify(content).length;
  }

  /**
   * Update last known state
   * @param {string} subcategoryId - ID of the subcategory
   * @param {any} newState - New state
   */
  updateLastState(subcategoryId, newState) {
    this.lastKnownState.set(subcategoryId, this.deepClone(newState));
    this.lastChangeTime.set(subcategoryId, Date.now());
  }

  /**
   * Deep clone an object
   * @param {any} obj - Object to clone
   * @returns {any} - Cloned object
   */
  deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.deepClone(item));
    }
    
    const cloned = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        cloned[key] = this.deepClone(obj[key]);
      }
    }
    
    return cloned;
  }

  /**
   * Check for generic changes (fallback)
   * @param {string} subcategoryId - ID of the subcategory
   * @param {any} newContent - New content
   * @returns {boolean} - Whether there's a change
   */
  hasGenericChange(subcategoryId, newContent) {
    const lastState = this.lastKnownState.get(subcategoryId);
    
    if (!lastState) {
      this.lastKnownState.set(subcategoryId, this.deepClone(newContent));
      return false;
    }
    
    const oldStr = JSON.stringify(lastState);
    const newStr = JSON.stringify(newContent);
    
    if (oldStr !== newStr) {
      this.updateLastState(subcategoryId, newContent);
      return true;
    }
    
    return false;
  }

  /**
   * Reset state for a subcategory (useful for manual saves)
   * @param {string} subcategoryId - ID of the subcategory
   */
  resetState(subcategoryId) {
    this.lastKnownState.delete(subcategoryId);
    this.lastChangeTime.delete(subcategoryId);
  }

  /**
   * Get current state for a subcategory
   * @param {string} subcategoryId - ID of the subcategory
   * @returns {any} - Current state
   */
  getCurrentState(subcategoryId) {
    return this.lastKnownState.get(subcategoryId);
  }

  /**
   * Clear all states (for cleanup)
   */
  clearAll() {
    this.lastKnownState.clear();
    this.lastChangeTime.clear();
  }
}

// Create singleton instance
const changeDetector = new ChangeDetector();

export default changeDetector;
